import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    submitEntrepreneurForm,
    updateEntrepreneurForm,
    resetState,
    fetchEntrepreneurForm
} from "../../store/userSlice";
import cities from "../../assets/json/cities.json";
import SingleFileUpload from "../fileUpload/FileUpload";
import districts from "../../assets/json/districts.json";
import { getFileLink } from "../../utils/getFileLink";
import banks from "../../assets/json/banks.json"; // Banka listesini banks.json'dan okuyalım
import sectors from "../../assets/json/sectors.json";

const countries = [
    { name: "Türkiye", code: "TR" },
    { name: "Amerika Birleşik Devletleri", code: "US" },
    { name: "Almanya", code: "DE" },
    { name: "Fransa", code: "FR" },
    { name: "İngiltere", code: "UK" },
];

const EntrepreneurForm = () => {
    const dispatch = useDispatch();
    const { loading, error, success, entrepreneurInfo } = useSelector((state) => state.user);

    const [errors, setErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [formData, setFormData] = useState({
        countryCode: "",
        city: "",
        district: "",
        postalCode: "",
        address: "",
        educationStatus: "",
        job: "",
        sector: "",
        bank: "",
        iban: "",
        cvFilePath: "",
    });

    const [availableDistricts, setAvailableDistricts] = useState([]);

    useEffect(() => {
        dispatch(fetchEntrepreneurForm())
            .unwrap()
            .then((response) => {
                setFormData({
                    countryCode: response.countryCode || "",
                    city: response.city || "",
                    district: response.district || "",
                    postalCode: response.postalCode || "",
                    address: response.address || "",
                    educationStatus: response.educationStatus || "",
                    job: response.job || "",
                    sector: response.sector || "",
                    bank: response.bank || "",
                    iban: response.iban || "",
                    cvFilePath: response.cvFilePath || "",
                });

                if (response.city) {
                    const selectedCity = cities.find(city => city.name === response.city);
                    if (selectedCity) {
                        const cityDistricts = districts.filter(
                            district => district.il_id === selectedCity.id
                        );
                        setAvailableDistricts(cityDistricts);
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setPageLoading(false);
            });
    }, [dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        console.log(`Input değişti: ${name} = ${value}`); // Debug log

        const newFormData = {
            ...formData,
            [name]: value,
        };

        // When city changes, update districts and reset district selection
        if (name === 'city') {
            const selectedCity = cities.find(city => city.name === value);
            if (selectedCity) {
                const cityDistricts = districts.filter(
                    district => district.il_id === selectedCity.id
                );
                setAvailableDistricts(cityDistricts);
                newFormData.district = ''; // Reset district selection
            } else {
                setAvailableDistricts([]);
            }
        }

        setFormData(newFormData);
        validateForm(newFormData);
    };

    const handleCountryChange = (e) => {
        console.log('Ülke seçimi değişti:', e.target.value); // Debug log

        const selectedCountry = countries.find(
            (country) => country.name === e.target.value
        );
        const newFormData = {
            ...formData,
            countryCode: selectedCountry ? selectedCountry.code : "",
        };
        setFormData(newFormData);
        validateForm(newFormData);
    };

    const validateForm = (dataToValidate = formData) => {
        let tempErrors = {};
        let isValid = true;

        console.log('Validation başladı:', dataToValidate); // Debug log

        Object.keys(dataToValidate).forEach((key) => {
            const value = dataToValidate[key];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                tempErrors[key] = 'Bu alan zorunludur';
                isValid = false;
                console.log(`Hata: ${key} alanı boş`); // Debug log
            }
        });

        if (dataToValidate.iban && !/^TR\d{24}$/.test(dataToValidate.iban.replace(/\s/g, ''))) {
            tempErrors.iban = 'Geçerli bir IBAN giriniz (TR ile başlayan 22 karakter)';
            isValid = false;
            console.log('IBAN formatı hatalı'); // Debug log
        }

        if (dataToValidate.postalCode && !/^\d{5}$/.test(dataToValidate.postalCode)) {
            tempErrors.postalCode = '5 haneli posta kodu giriniz';
            isValid = false;
            console.log('Posta kodu formatı hatalı'); // Debug log
        }

        console.log('Validation sonucu:', { isValid, errors: tempErrors }); // Debug log

        setErrors(tempErrors);
        setIsFormValid(isValid);
        return isValid;
    };

    const handleDocumentUploaded = (filePath) => {
        console.log('Dosya yüklendi:', filePath); // Debug log
        setFormData({ ...formData, cvFilePath: filePath });
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submit edildi'); // Debug log
        console.log('Form data:', formData); // Debug log

        if (!validateForm()) {
            console.log('Validation başarısız'); // Debug log
            toast.error("Lütfen tüm alanları doldurun ve hataları düzeltin.");
            return;
        }

        const action = entrepreneurInfo ? updateEntrepreneurForm : submitEntrepreneurForm;

        dispatch(action(formData))
            .unwrap()
            .then((response) => {
                toast.success("İşlem başarıyla gerçekleştirildi!");
                dispatch(resetState());

                // Form verilerini response ile güncelle
                if (response && response.entrepreneurInfo) {
                    setFormData({
                        countryCode: response.entrepreneurInfo.countryCode || "",
                        city: response.entrepreneurInfo.city || "",
                        district: response.entrepreneurInfo.district || "",
                        postalCode: response.entrepreneurInfo.postalCode || "",
                        address: response.entrepreneurInfo.address || "",
                        educationStatus: response.entrepreneurInfo.educationStatus || "",
                        job: response.entrepreneurInfo.job || "",
                        sector: response.entrepreneurInfo.sector || "",
                        bank: response.entrepreneurInfo.bank || "",
                        iban: response.entrepreneurInfo.iban || "",
                        cvFilePath: response.entrepreneurInfo.cvFilePath || "",
                    });
                }
            })
            .catch((err) => {
                toast.error(err || "Form gönderimi başarısız.");
            });
    };

    // Return loading spinner while page is loading
    if (pageLoading) {
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh' 
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
            <Typography variant="h6" gutterBottom>
                Girişimci Formu
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">Bilgiler başarıyla kaydedildi!</Alert>}
            <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                    {/* Ülke ve Şehir - 2 sütun */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label="Ülke"
                            name="countryCode"
                            select
                            fullWidth
                            value={countries.find((country) => country.code === formData.countryCode)?.name || ""}
                            onChange={handleCountryChange}
                            error={!!errors.countryCode}
                            helperText={errors.countryCode}
                        >
                            {countries.map((country) => (
                                <MenuItem key={country.code} value={country.name}>
                                    {country.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Şehir"
                            name="city"
                            fullWidth
                            value={formData.city}
                            onChange={handleChange}
                            error={!!errors.city}
                            helperText={errors.city}
                        >
                            <MenuItem value="">Seçiniz</MenuItem>
                            {cities.map((city) => (
                                <MenuItem key={city.id} value={city.name}>
                                    {city.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* İlçe ve Posta Kodu - 2 sütun */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            select
                            label="İlçe"
                            name="district"
                            fullWidth
                            value={formData.district}
                            onChange={handleChange}
                            error={!!errors.district}
                            helperText={errors.district}
                            disabled={!formData.city}
                        >
                            <MenuItem value="">Seçiniz</MenuItem>
                            {availableDistricts.map((district) => (
                                <MenuItem key={district.id} value={district.name}>
                                    {district.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Posta Kodu"
                            name="postalCode"
                            fullWidth
                            value={formData.postalCode}
                            onChange={handleChange}
                            error={!!errors.postalCode}
                            helperText={errors.postalCode}
                        />
                    </Box>

                    {/* Adres - tek satır */}
                    <TextField
                        label="Adres"
                        name="address"
                        fullWidth
                        value={formData.address}
                        onChange={handleChange}
                        error={!!errors.address}
                        helperText={errors.address}
                    />

                    {/* Eğitim Durumu, Meslek ve Sektör - 3 sütun */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                        <TextField
                            label="Eğitim Durumu"
                            name="educationStatus"
                            select
                            fullWidth
                            value={formData.educationStatus}
                            onChange={handleChange}
                            error={!!errors.educationStatus}
                            helperText={errors.educationStatus}
                        >
                            <MenuItem value="Lise">Lise</MenuItem>
                            <MenuItem value="Lisans">Lisans</MenuItem>
                            <MenuItem value="Yüksek Lisans">Yüksek Lisans</MenuItem>
                            <MenuItem value="Doktora">Doktora</MenuItem>
                        </TextField>

                        <TextField
                            label="Meslek"
                            name="job"
                            fullWidth
                            value={formData.job}
                            onChange={handleChange}
                            error={!!errors.job}
                            helperText={errors.job}
                        />

                        <TextField
                            label="Faaliyet Gösteren Sektör"
                            name="sector"
                            select
                            fullWidth
                            value={formData.sector}
                            onChange={handleChange}
                            error={!!errors.sector}
                            helperText={errors.sector}
                        >
                            {sectors.map((sectorObj) => (
                                <MenuItem key={sectorObj.value} value={sectorObj.value}>
                                    {sectorObj.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* Banka ve IBAN - 2 sütun */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label="Banka"
                            name="bank"
                            select
                            fullWidth
                            value={formData.bank}
                            onChange={handleChange}
                            error={!!errors.bank}
                            helperText={errors.bank}
                        >
                            {banks.map((bankItem) => (
                                <MenuItem key={bankItem.code} value={bankItem.name}>
                                    {bankItem.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="IBAN"
                            name="iban"
                            fullWidth
                            value={formData.iban}
                            onChange={handleChange}
                            error={!!errors.iban}
                            helperText={errors.iban}
                        />
                    </Box>
                    <Typography variant="h8" gutterBottom sx={{ marginTop: 2 }}>CV</Typography>

                    {/* CV Dosya Yolu - tek satır */}
                    <SingleFileUpload onUploadComplete={handleDocumentUploaded} filePath={getFileLink(formData.cvFilePath)}/>
                </Box>

                <Box sx={{ textAlign: "right", marginTop: 4 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={loading || !isFormValid}
                    >
                        {loading ? <CircularProgress size={24} /> : "Kaydet"}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default EntrepreneurForm;
